import { AddBox } from '@mui/icons-material';
import { Box, Chip, Grid, Icon, IconButton, Tooltip, Typography } from '@mui/material';
import { format, parseISO } from 'date-fns';
import React from 'react';
import { connect } from 'react-redux';
import { Observable } from 'rxjs';

import { EntityTextField } from 'app/components/form-controls/entity-text-field';
import { EnumSelector } from 'app/components/form-controls/enum-selector';
import { If } from 'app/components/if';
import { RequestStatus } from 'app/components/request-status';
import { RequireRole } from 'app/components/require-role';
import { Translate } from 'app/components/translate';
import { UserPicker } from 'app/components/user-picker';
import { Role } from 'app/constants/role';
import { SeamStatus } from 'app/constants/seam-status';
import { PriorityEnum, PriorityEnumDict } from 'app/enum';
import { User } from 'app/model';
import { Comment } from 'app/model/comment';
import { CrisRequest } from 'app/model/cris-request';
import { RestfulModel } from 'app/model/restful-model';
import { crisRequestService } from 'app/services/cris-request.service';
import { messagingService } from 'app/services/messaging.service';
import { RootState } from 'app/store';
import { CrisAddSeamDialog } from 'features/cris/request/cris-add-seam-dialog';

import { Comments } from '../../comments/comments';
import { RequestTable } from '../../seam/request-table';
import {
    addCrisComment,
    loadCrisComments,
    selectCrisComments,
    setCrisComments,
    setCurrentCrisRequest,
} from '../cris.slice';

interface CrisSummaryProps {
    request: CrisRequest;

    // Redux Propx
    crisComments: Comment[];

    loadCrisComments: (request: RestfulModel) => Observable<Comment[]>;

    setCrisComments: (comments: Comment[]) => void;

    addCrisComment: (comment: string, request: CrisRequest) => Observable<Comment>;

    setCurrentCrisRequest: (data: CrisRequest) => void;
}

interface CrisSummaryState {
    refreshTrigger: number;
    openSeamDialog: boolean;
}

class CrisSummary extends React.Component<CrisSummaryProps, CrisSummaryState> {
    public constructor(props: CrisSummaryProps | Readonly<CrisSummaryProps>) {
        super(props);

        this.state = { refreshTrigger: 1, openSeamDialog: false };
    }

    public componentDidMount() {
        this.props.loadCrisComments(this.props.request).subscribe((value) => {
            this.props.setCrisComments(value);
        });
    }

    public render(): React.ReactNode {
        const request = this.props.request;

        const path = window.location.href.replace('/summary', '');

        return (
            <Box display='flex' flexDirection='column'>
                <Grid container spacing={2} className='my-3'>
                    <Grid item xs={3}>
                        <Translate tkey='forms:Data Element' />:
                    </Grid>
                    <Grid item xs={3}>
                        <strong>{CrisSummary.getDataElementName(request)}</strong>
                    </Grid>
                    <Grid item xs={3}>
                        <Translate tkey='forms:Requested Element Name' />:
                    </Grid>
                    <Grid item xs={3}>
                        <strong>{request.elementName}</strong>
                    </Grid>
                    <Grid item xs={3}>
                        <Translate tkey='forms:Requested Due Date' />:
                    </Grid>
                    <Grid item xs={3}>
                        <strong>{request.dueDate ? format(parseISO(request.dueDate), 'MM/dd/yyyy') : '(none)'}</strong>
                    </Grid>
                    <Grid item xs={3}>
                        <Translate tkey='forms:Creation Date' />:
                    </Grid>
                    <Grid item xs={3}>
                        <strong>{format(parseISO(request.dateCreated), 'MM/dd/yyyy')}</strong>
                    </Grid>
                    <Grid item xs={3}>
                        <Translate tkey='forms:Request Status' />:
                    </Grid>
                    <Grid item xs={3}>
                        <RequestStatus status={request.currentPlace} />
                    </Grid>
                    <Grid item xs={6}></Grid>
                    <RequireRole roles={Role.CRIS_USER}>
                        <Grid item xs={3}>
                            <EntityTextField
                                label={<Translate tkey='forms:ETA' />}
                                type='date'
                                className='mb-1'
                                name='estimatedDelivery'
                                onChange={(evt, name, value) => this.patchValue(name, value)}
                                value={request}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <EnumSelector
                                label={<Translate tkey='forms:Tagger Priority' />}
                                name='taggerPriority'
                                onChange={(event, name, val) => this.patchValue(name, val)}
                                value={request}
                                sx={{ width: '14em' }}
                                theEnum={PriorityEnum}
                                labelDictionary={PriorityEnumDict}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <UserPicker
                                label='Reassign to User'
                                variant='standard'
                                onChange={(evt, user) => this.reassignUser(user)}
                                helperText='Will reassign all SEAM requests to this user'
                            />
                        </Grid>
                    </RequireRole>
                </Grid>
                <Grid container spacing={2}>
                    <If condition={!!request.jiraLink}>
                        <Grid item xs={3}>
                            <Translate tkey='forms:JIRA Task' />:
                        </Grid>
                        <Grid item xs={9}>
                            <strong>
                                <a href={request.jiraLink}>{request.jiraLink}</a>
                            </strong>{' '}
                            {CrisSummary.clipCopyIcon(request.jiraLink)}
                        </Grid>
                    </If>
                    <Grid item xs={3}>
                        <Translate tkey='forms:CRIS Task' />:
                    </Grid>
                    <Grid item xs={9}>
                        <strong>
                            <a href={path}>{path}</a>
                        </strong>{' '}
                        {CrisSummary.clipCopyIcon(path)}
                    </Grid>
                </Grid>
                <Typography variant='body1' className='mt-3 mb-1'>
                    <Translate tkey='headings:Description' />
                </Typography>
                <Typography variant='body1'>{request.description || '(blank)'}</Typography>
                <hr />
                <If condition={SeamStatus.TXN_CHECK !== request.currentPlace}>
                    <Box display='flex' flexDirection='row' alignItems='center'>
                        <Typography variant='h4'>Related Seam Requests</Typography>
                        <RequireRole roles={Role.SEAM_REQUESTS}>
                            <Tooltip title='Create Another Seam Request'>
                                <IconButton
                                    color='primary'
                                    size='large'
                                    onClick={() => this.setState({ openSeamDialog: true })}
                                >
                                    <AddBox fontSize='inherit' />
                                </IconButton>
                            </Tooltip>
                        </RequireRole>
                    </Box>

                    <CrisAddSeamDialog
                        open={this.state.openSeamDialog}
                        crisRequest={request}
                        onClose={() =>
                            this.setState({ openSeamDialog: false, refreshTrigger: this.state.refreshTrigger + 1 })
                        }
                    />
                    <RequestTable filter={{ cris: request.id }} refreshTrigger={this.state.refreshTrigger} />
                </If>
                <Box className='my-2'>
                    <Typography variant='h6'>Comments</Typography>
                    <Comments
                        comments={this.props.crisComments}
                        addComment={(comment) => this.props.addCrisComment(comment, request).subscribe()}
                    />
                </Box>
            </Box>
        );
    }

    private patchValue(name: keyof CrisRequest, value: unknown) {
        crisRequestService
            .patch(this.props.request, {
                [name]: value,
            })
            .subscribe(() => messagingService.showSuccess('Saved'));

        this.props.setCurrentCrisRequest({
            ...this.props.request,
            [name]: value,
        });
    }

    private reassignUser(user: User | undefined) {
        if (!user) {
            return;
        }

        crisRequestService.reassign(this.props.request, user).subscribe(() => {
            messagingService.showSuccess('Reassigned');
            this.setState({
                ...this.state,
                refreshTrigger: this.state.refreshTrigger + 1,
            });
        });
    }

    private static clipCopyIcon(data: string): React.ReactNode {
        return (
            <Tooltip title='Copy to Clipboard'>
                <IconButton
                    onClick={() => {
                        void navigator.clipboard.writeText(data);
                    }}
                    size='small'
                >
                    <Icon fontSize='small'>content_copy</Icon>
                </IconButton>
            </Tooltip>
        );
    }

    private static getDataElementName(request: CrisRequest) {
        if (!request.storeData || 0 === request.storeData.length) {
            if (!request.brand) {
                return <Typography display='inline'>[None]</Typography>;
            }

            return (
                <React.Fragment>
                    <Typography display='inline'>{request.brand.name} </Typography>
                    <Chip label='Brand' color='primary' size='small' />
                </React.Fragment>
            );
        }

        return (
            <React.Fragment>
                <Typography display='inline'>
                    {request.storeData[0]?.name} ({request.brand?.name || ''}){' '}
                </Typography>
                <Chip label='Store' color='primary' size='small' />
            </React.Fragment>
        );
    }
}

const mapStateToProps = (state: RootState) => ({
    crisComments: selectCrisComments(state),
});

const connected = connect(mapStateToProps, {
    loadCrisComments,
    setCrisComments,
    addCrisComment,
    setCurrentCrisRequest,
})(CrisSummary);

export { connected as CrisSummary };
